<template>
  <!-- Totals card for a dedicated pre-paid Poola carton. The goods are paid for
       at claim time — cash from her wallet plus a PoolShare burn — so the only
       thing the carton can ever bill is the club's 5% service commission, and
       the invoice total is 0 once that is waived. The headline is therefore the
       NET cash she actually paid (the box's total_amount), never the invoice
       total: leading a receipt for 1,854.00 of goods with a 0.00 would be a lie
       by omission. The receipt below it carries the rest of the story. -->
  <f7-card class="poola-paid-card elevation-4">
    <f7-card-content class="poola-paid-inner">
      <div class="poola-paid-label">{{ $t('box.totals.poola.paidLabel') }}</div>
      <div class="poola-paid-value">{{ formatCurrency(amountCents) }}</div>
      <div v-if="itemCount > 0" class="poola-paid-meta">
        {{ $t('box.totals.lineCount', { count: itemCount }) }}
      </div>
      <div class="poola-paid-hint">{{ $t('box.totals.poola.paidHint') }}</div>
    </f7-card-content>

    <!-- Closed-invoice receipt — once the carton has settled, the member gets
         the line-by-line breakdown of what she paid, not just the headline. -->
    <template v-if="invoiceNumber">
      <template v-if="items?.length">
        <div class="poola-invoice-divider" />
        <f7-card-content class="poola-items-header">
          <span class="poola-items-heading">{{ $t('box.totals.items') }}</span>
          <span class="poola-items-count">{{ items.length }}</span>
        </f7-card-content>
        <f7-list
          media-list
          no-hairlines
          no-hairlines-between
          class="poola-items-list"
        >
          <f7-list-item
            v-for="item in items"
            :key="item.id"
            :title="itemDisplayName(item)"
          >
            <template #media>
              <!-- Manual (off-catalog) lines have no catalog thumbnail — render
                   the branded monogram default instead of a broken <img>. -->
              <manual-item-avatar
                v-if="item.isManual"
                :label="itemDisplayName(item)"
                :size="48"
                :alt="$t('item.manual.avatarAlt')"
              ></manual-item-avatar>
              <img
                v-else
                :src="item.avatarUrl ?? item.productAvatarUrl"
                class="poola-item-thumb"
                :alt="itemDisplayName(item)"
              />
            </template>
            <template
              v-if="item.discount > 0"
              #subtitle
            >
              <span class="poola-item-secondary">
                <!-- dir="ltr" isolates the fragment: a bare "×2" in an RTL
                     line has no strong character, so the browser reorders it
                     to "2×". -->
                <span
                  v-if="item.qty > 1"
                  dir="ltr"
                  class="poola-item-fact"
                >×{{ item.qty }}</span>
                <span class="poola-item-fact">
                  {{ $t('box.totals.poola.shopPrice') }} {{ formatAmount(item.price * item.qty) }}
                </span>
                <span class="poola-item-fact">
                  {{ $t('box.totals.poola.poolshareCovered') }} {{ formatAmount(-item.discount) }}
                </span>
              </span>
            </template>
            <template #after>
              <div class="poola-item-amount">{{ formatCurrency(lineAmount(item)) }}</div>
            </template>
          </f7-list-item>
        </f7-list>
      </template>

      <div class="poola-invoice-divider" />
      <f7-card-content class="poola-invoice-rows">
        <div
          v-if="commissionRateBp > 0"
          class="poola-invoice-row"
        >
          <span class="poola-invoice-row-label">
            {{ $t('box.totals.commission') }}
            <span class="poola-invoice-row-rate">({{ commissionRatePct }}%)</span>
          </span>
          <span class="poola-invoice-row-value">{{ formatCurrency(commissionCents) }}</span>
        </div>

        <div
          v-for="(entry, index) in visibleAdjustments"
          :key="index"
          class="poola-invoice-row poola-invoice-row-adjustment"
          :class="{ 'poola-invoice-row-negative': entry.amount < 0 }"
        >
          <span class="poola-invoice-row-label">{{ entry.label }}</span>
          <span class="poola-invoice-row-value">{{ formatSignedCurrency(entry.amount) }}</span>
        </div>

        <div class="poola-invoice-row poola-invoice-row-due">
          <span class="poola-invoice-row-label">{{ $t('box.totals.poola.dueNow') }}</span>
          <span class="poola-invoice-row-value">{{ formatCurrency(dueCents) }}</span>
        </div>
      </f7-card-content>
    </template>
  </f7-card>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ManualItemAvatar from '@/components/orders/ManualItemAvatar.vue';
import { useCurrency } from '@/composables/useCurrency.js';
import { itemDisplayName } from '@/utils/orderBoxItemDisplay.js';

const props = defineProps({
  // Net cash paid, in cents (the box's total_amount).
  amountCents: { type: Number, default: 0 },
  // Number of claimed pieces in the carton.
  itemCount: { type: Number, default: 0 },
  // Gates the closed-invoice receipt below the headline. Only a settled
  // carton (backend commit 22ee9d8b) carries an invoice number.
  invoiceNumber: { type: [String, Number], default: null },
  // The box's order-box-item lines, for the per-line receipt rows.
  items: { type: Array, default: () => [] },
  // The club's service commission, in cents.
  commissionCents: { type: Number, default: 0 },
  // Commission rate in basis points (500 = 5%). Gates the whole commission row.
  commissionRateBp: { type: Number, default: 0 },
  // Per-line invoice adjustment entries (e.g. the commission waiver, rounding).
  // Shape per entry: { type, label, amount_cents|amountCents }.
  adjustments: { type: Array, default: () => [] },
  // What she still owes after commission + adjustments. The receipt's
  // punchline row.
  dueCents: { type: Number, default: 0 },
});

const { t } = useI18n();
const { formatCurrency, formatAmount, formatSignedCurrency } = useCurrency();

const commissionRatePct = computed(() => {
  const bp = props.commissionRateBp ?? 0;
  return Number((bp / 100).toFixed(2));
});

// Tolerates both amount_cents (api) and amountCents (locally constructed).
// The system rounding_down entry carries an English backend label, which must
// never reach an Arabic receipt — swapped for the i18n key instead, mirroring
// BoxTotalsBreakdown's same rule. Zero-amount entries are dropped: nothing to
// show.
const visibleAdjustments = computed(() =>
  (props.adjustments ?? [])
    .map((entry) => {
      const amount = Number(entry?.amount_cents ?? entry?.amountCents ?? 0);
      const label = entry?.type === 'rounding_down'
        ? t('box.totals.roundingDown')
        : (entry?.label ?? '');
      return { amount, label };
    })
    .filter((entry) => entry.amount !== 0),
);

// The line's net cash paid. Prefers the backend-computed `amount`; falls back
// to price*qty − discount for legacy payloads that predate the field.
function lineAmount(item) {
  if (item?.amount != null) return item.amount;
  return (item?.price ?? 0) * (item?.qty ?? 0) - (item?.discount ?? 0);
}


</script>

<style scoped>
.poola-paid-inner {
    text-align: center;
    padding: 20px 16px;
}

.poola-paid-label {
    font-size: 12px;
    font-weight: var(--poolit-font-weight-semibold);
    color: var(--poolit-text-tertiary);
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.poola-paid-value {
    margin-top: 4px;
    font-size: 28px;
    font-weight: var(--poolit-font-weight-bold);
    color: var(--poolit-primary-600);
    line-height: 1.1;
}

.poola-paid-meta {
    margin-top: 6px;
    font-size: 12px;
    color: var(--poolit-text-tertiary);
}

.poola-paid-hint {
    margin-top: 12px;
    font-size: 13px;
    line-height: 1.5;
    color: var(--poolit-text-secondary);
}

.poola-invoice-divider {
    height: 1px;
    background: var(--poolit-border-color);
    margin: 0;
}

.poola-items-header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    padding: 12px 16px 4px;
}

.poola-items-heading {
    font-size: 12px;
    font-weight: var(--poolit-font-weight-semibold);
    color: var(--poolit-text-tertiary);
    text-transform: uppercase;
    letter-spacing: 0.6px;
}

.poola-items-count {
    font-size: 13px;
    font-weight: var(--poolit-font-weight-semibold);
    color: var(--poolit-text-secondary);
    font-feature-settings: 'tnum' on, 'lnum' on;
}

.poola-items-list {
    margin: 0;
}

.poola-item-thumb {
    width: 48px;
    height: 48px;
    border-radius: 8px;
    object-fit: cover;
}

.poola-item-secondary {
    display: block;
    font-size: 11px;
    line-height: 1.4;
    color: var(--poolit-text-tertiary);
    /* Wraps rather than truncates. F7's media-list puts the subtitle on its
       own full-width line under the title row, so at 375px all three facts
       fit on one line — but a long name or quantity must never be allowed
       to clip the saving, which is the whole reason the line exists. */
    white-space: normal;
}

/* The · separators live in CSS so each fact stays its own element and the
   ×N fragment can carry its own dir. */
.poola-item-fact + .poola-item-fact::before {
    content: '\00b7';
    margin-inline: 4px;
}

.poola-item-amount {
    /* Bigger and bolder than the two secondary numbers under it: of the three
       numbers on the row, what she actually paid is the one she is looking
       for. margin-inline-start keeps it off the product name — F7 pads
       .item-after on the physical left, which is the wrong side in RTL and
       leaves the two touching. */
    margin-inline-start: 10px;
    font-size: 15px;
    font-weight: var(--poolit-font-weight-bold);
    color: var(--poolit-text-primary);
    font-feature-settings: 'tnum' on, 'lnum' on;
}

.poola-invoice-rows {
    padding: var(--poolit-spacing-md);
    display: flex;
    flex-direction: column;
    gap: var(--poolit-spacing-sm);
}

.poola-invoice-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: var(--poolit-spacing-md);
}

.poola-invoice-row-label {
    font-size: 14px;
    color: var(--poolit-text-secondary);
}

.poola-invoice-row-rate {
    color: var(--poolit-text-tertiary);
    font-size: 12px;
    margin-inline-start: 4px;
}

.poola-invoice-row-value {
    font-size: 15px;
    font-weight: var(--poolit-font-weight-semibold);
    color: var(--poolit-text-primary);
    font-feature-settings: 'tnum' on, 'lnum' on;
}

.poola-invoice-row-adjustment .poola-invoice-row-label {
    font-size: 13px;
}

.poola-invoice-row-adjustment.poola-invoice-row-negative .poola-invoice-row-value {
    color: var(--poolit-success-600, #1b8a3b);
}

.poola-invoice-row-due {
    margin-top: 4px;
    padding-top: var(--poolit-spacing-sm);
    border-top: 1px solid var(--poolit-border-color);
}

.poola-invoice-row-due .poola-invoice-row-label {
    font-size: 15px;
    font-weight: var(--poolit-font-weight-bold);
    color: var(--poolit-text-primary);
}

.poola-invoice-row-due .poola-invoice-row-value {
    font-size: 17px;
    font-weight: var(--poolit-font-weight-bold);
}
</style>
